/**
 * Rescue-portal-specific GraphQL queries.
 *
 * These extend the shared appview queries with extra fields the rescue portal
 * needs (e.g. currentUser.email for ownership detection).
 */

import { gql } from '@apollo/client';
import type { ResellerTaxExemptType, viewingChips } from '@/types/gql-op-types';
import {
  miniChipFragment,
  basePetFragment,
  contactInfoFragment,
  renderFragments,
  baseChipFragment,
} from '../../lib/fragments';

// ── Response type ───────────────────────────────
// Extends the generated `viewingChips` type with extra fields fetched by
// our rescue-specific query.

type BaseChipItem = viewingChips['currentUser']['chipReseller'] extends infer R
  ? R extends { chips: { chips: Array<infer C> } }
    ? C
    : never
  : never;

type RescueChipItem = BaseChipItem & {
  latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } } | null;
};

export interface RescueViewingChipsData {
  currentUser: {
    id: string;
    email: string;
    chipReseller?: {
      id: string;
      name: string;
      chips: {
        chips: RescueChipItem[];
        metadata?: {
          limit: number;
          offset: number;
          totalChipCount: number;
        } | null;
      };
    } | null;
  };
}

// ── Query ───────────────────────────────────────

/**
 * Like the shared `viewingChipsQuery` but also fetches:
 *  - `currentUser.email` — used to compare against chip contact email
 *  - `latestContactHistoryEntry.contactInfo.primaryEmail` — merged with the
 *     miniChip fragment so we can determine rescue vs adopter ownership at
 *     the list level.
 */
export const rescueViewingChipsQuery = gql`
  query rescueViewingChips($statusType: ChipStatusType!, $limit: Int, $offset: Int) {
    currentUser {
      id
      email
      chipReseller {
        id
        name
        chips(statusType: $statusType, limit: $limit, offset: $offset) {
          chips {
            ...miniChip
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
          metadata {
            limit
            offset
            totalChipCount
          }
        }
      }
    }
  }

  ${renderFragments(miniChipFragment)}
`;

// ── Search query ──

export interface RescueSearchChipsData {
  currentUser: {
    id: string;
    email: string;
    chipReseller?: {
      id: string;
      searchChips: {
        chips: RescueChipItem[];
      };
    } | null;
  };
}

export const rescueSearchChipsQuery = gql`
  query rescueSearchChips($input: ChipQueryInput!) {
    currentUser {
      id
      email
      chipReseller {
        id
        searchChips(query: $input) {
          chips {
            ...miniChip
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
        }
      }
    }
  }

  ${renderFragments(miniChipFragment)}
`;

// ── Chip stats (lightweight aggregate counts) ──

export interface RescueChipStatsData {
  currentUser: {
    id: string;
    chipReseller?: {
      id: string;
      stats: {
        totalStock: number;
        totalRegistered: number;
      };
    } | null;
  };
}

/**
 * Lightweight query for aggregate chip counts on the Dashboard.
 *
 * Uses the `stats` resolver on ChipReseller which runs efficient parallel
 * COUNT queries and derives `totalStock` without expensive NOT EXISTS
 * subqueries.  Much faster (and more reliable under tight statement
 * timeouts) than pulling `metadata.totalChipCount` from the paginated
 * `chips` field.
 */
export const rescueChipStatsQuery = gql`
  query rescueChipStats {
    currentUser {
      id
      chipReseller {
        id
        stats {
          totalStock
          totalRegistered
        }
      }
    }
  }
`;

// ── External chip lookup (for pet lookup modal) ──

/**
 * Uses `resellerChip` to look up any chip — available to verified rescues.
 * Returns full pet info, unredacted owner contact info, and the owning
 * reseller's name (organization).
 */
export const externalChipLookupQuery = gql`
  query externalChipLookup($id: ID!) {
    resellerChip(id: $id) {
      id
      shortId
      serialNumber
      status
      updatedAt
      pet {
        ...basePet
      }
      latestContactHistoryEntry {
        source
        contactInfo {
          ...contactInfo
        }
      }
      reseller {
        id
        name
      }
    }
  }

  ${renderFragments(basePetFragment, contactInfoFragment)}
`;

/**
 * Uses `chip` to look up any chip — that may exist but is only available to verified rescues.
 * Returns minimal info
 */
export const externalChipForUnverifiedResellerLookupQuery = gql`
  query chipDetailsExternalLookup($id: ID!) {
    chip(id: $id) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

/**
 * Batch chip-availability lookup for the rescue portal's CSV import
 * wizard. Sends up to 500 serials and gets back a server-side
 * classification (IN_MY_INVENTORY / CLAIMABLE / OTHER_RESELLER /
 * NOT_FOUND / LINKED_TO_PET / NOT_USABLE) per serial. The classification
 * is relative to the authenticated caller's reseller — no
 * `currentResellerId` needs to be sent. Read-only / advisory; the actual
 * claim happens later, per-row, in the async import worker.
 *
 * Backend normalizes the returned `serialNumber` to uppercase, so the
 * frontend should also key results by uppercase serials to match.
 */
export const rescueChipClaimEligibilityQuery = gql`
  query rescueChipClaimEligibility($serialNumbers: [String!]!) {
    rescueChipClaimEligibility(serialNumbers: $serialNumbers) {
      serialNumber
      status
    }
  }
`;

export type RescueChipClaimStatus =
  | 'IN_MY_INVENTORY'
  | 'CLAIMABLE'
  | 'OTHER_RESELLER'
  | 'NOT_FOUND'
  /** Chip exists and is already attached to a pet — re-linking would
   *  detach the existing registration. Backend pre-flight rejects;
   *  surfacing it at upload time saves a publish round-trip. */
  | 'LINKED_TO_PET'
  /** Chip exists but is in a condition that prevents import (recalled,
   *  damaged, decommissioned, etc.). Backend pre-flight rejects; we
   *  surface it inline so the user doesn't burn a publish round-trip. */
  | 'NOT_USABLE';

export interface RescueChipClaimEligibility {
  serialNumber: string;
  status: RescueChipClaimStatus;
}

export interface RescueChipClaimEligibilityData {
  rescueChipClaimEligibility: RescueChipClaimEligibility[];
}

// ── Rescue Devices (offset-paginated, device-bearing pets only) ──

export type RescueDeviceFilter = 'IN_RESCUE' | 'IN_FOSTER' | 'PENDING_ADOPTIONS' | 'COMPLETED_ADOPTIONS';

/**
 * Paginated list of rescue devices. Uses the dedicated `rescueDevices` query
 * which only returns pets with GPS devices and includes transfer details inline.
 */
export const rescueDevicesQuery = gql`
  query rescueDeviceList($input: RescueDevicesInput!) {
    rescueDevices(input: $input) {
      items {
        pet {
          id
          name
          gender
          species
          weight
          color
          additionalInfo
          yearOfBirth
          monthOfBirth
          dayOfBirth
          breed {
            name
          }
          chip {
            id
            shortId
            serialNumber
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
          device {
            id
            moduleId
            currentCollarModelLongName
            hasActiveSubscription
            lastConnectionState {
              date
            }
          }
        }
        transfer {
          id
          status
          type
          toFullName
          toEmail
          toPhone
          completedAt
          createdAt
          updatedAt
        }
        batteryPercent
        isOnline
        hasSafeZone
        hasWifiSetup
      }
      totalCount
      hasMore
    }
  }
`;

export interface RescueDeviceItemPetDeviceGql {
  id: string;
  moduleId: string;
  currentCollarModelLongName?: string | null;
  hasActiveSubscription: boolean;
  lastConnectionState?: {
    date: string;
  } | null;
}

export interface RescueDeviceItemPetGql {
  id: string;
  name: string;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  additionalInfo?: string | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  breed?: { name: string } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  device?: RescueDeviceItemPetDeviceGql | null;
}

export interface RescueDeviceItemTransferGql {
  id: string;
  status: string;
  type: string;
  toFullName?: string | null;
  toEmail?: string | null;
  toPhone?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
}

export interface RescueDeviceItemGql {
  pet: RescueDeviceItemPetGql;
  transfer?: RescueDeviceItemTransferGql | null;
  batteryPercent: number | null;
  isOnline: boolean;
  hasSafeZone: boolean;
  hasWifiSetup: boolean;
}

export interface RescueDevicesData {
  rescueDevices: {
    items: RescueDeviceItemGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

// ── Rescue Order Shipments (devices in transit) ──

export type RescueOrderShipmentStatus = 'ORDERED' | 'SHIPPED' | 'DELIVERED' | 'CANCELED';

export interface RescueOrderShipmentGql {
  orderNumbers: string[];
  shipmentStatus: RescueOrderShipmentStatus;
  deviceCount: number;
  orderDate: string;
  trackingNumber?: string | null;
}

export interface RescueOrderShipmentsData {
  rescueOrderShipments: RescueOrderShipmentGql[];
}

/**
 * Outstanding (non-delivered) shipments for the current rescue user. Each
 * shipment surfaces only what the rescue portal renders; orders with multiple
 * shipments will appear once per still-shipping shipment.
 */
export const rescueOrderShipmentsQuery = gql`
  query rescueOrderShipments {
    rescueOrderShipments {
      orderNumbers
      shipmentStatus
      deviceCount
      orderDate
      trackingNumber
    }
  }
`;

// ── Rescue Nano Shipments (chips in transit) ──

export interface RescueNanoShipmentGql {
  orderNumbers: string[];
  shipmentStatus: RescueOrderShipmentStatus;
  chipCount: number;
  orderDate: string;
  trackingNumber?: string | null;
}

export interface RescueNanoShipmentsData {
  rescueNanoShipments: RescueNanoShipmentGql[];
}

/**
 * Outstanding (non-delivered) nano shipments for the current rescue user.
 * Same in-flight semantics as `rescueOrderShipments` but limited to shipments
 * containing a Fi Nano line item, with chip counts in place of device counts.
 */
export const rescueNanoShipmentsQuery = gql`
  query rescueNanoShipments {
    rescueNanoShipments {
      orderNumbers
      shipmentStatus
      chipCount
      orderDate
      trackingNumber
    }
  }
`;

// ── GWA Dashboard Stats (lightweight) ────────────

export interface RescueGwaDashboardStatsData {
  rescueGwaProgram: {
    id: string;
    monthRedemptions: number;
    totalRedemptions: number;
  } | null;
}

/**
 * Lightweight query for the Dashboard's GWA Redemptions stat card.
 * Skips the expensive `recentRedemptions` field (which fans out into
 * per-adopter user/pet/breed lookups) — the full query on the GWA page
 * will pick those up when the user navigates there.
 */
export const rescueGwaDashboardStatsQuery = gql`
  query rescueGwaDashboardStats {
    rescueGwaProgram {
      id
      monthRedemptions
      totalRedemptions
    }
  }
`;

// ── Rescue Device Stats ──────────────────────────

/**
 * Aggregate counts for all filter categories. Used for the summary stats cards.
 * Returns accurate device-only counts in a single round-trip.
 */
export const rescueDeviceStatsQuery = gql`
  query rescueDeviceStats($completedSince: DateTime) {
    rescueDeviceStats(completedSince: $completedSince) {
      inRescueCount
      inFosterCount
      pendingAdoptionsCount
      completedAdoptionsCount
      totalActiveDevices
    }
  }
`;

export interface RescueDeviceStatsGql {
  inRescueCount: number;
  inFosterCount: number;
  pendingAdoptionsCount: number;
  completedAdoptionsCount: number;
  totalActiveDevices: number;
}

export interface RescueDeviceStatsData {
  rescueDeviceStats: RescueDeviceStatsGql;
}

// ── Rescue create device order (Order Devices modal) ──

export interface RescueOrgShippingAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export interface RescueCreateDeviceOrderInput {
  contactName: string;
  contactEmail: string;
  deviceQuantity: number;
  includeHardCases: boolean;
  shippingAddress: RescueOrgShippingAddress;
  notes?: string | null;
}

export interface RescueCreateDeviceOrderData {
  rescueCreateDeviceOrder: {
    ok: boolean;
    orderId: string;
  };
}

/**
 * Submit a device-order request for the current rescue. The backend issues a
 * synthetic intake number now and the canonical Order ID once real orders
 * back this mutation.
 */
export const rescueCreateDeviceOrderMutation = gql`
  mutation rescueCreateDeviceOrder($input: RescueCreateDeviceOrderInput!) {
    rescueCreateDeviceOrder(input: $input) {
      ok
      orderId
    }
  }
`;

// ── Rescue create nano order (Order Nanos modal) ──

export interface RescueCreateNanoOrderInput {
  contactName: string;
  contactEmail: string;
  /** Number of 20-chip boxes (Fi Nano `F1-N-20` SKU). Backend bounds: 1–25. */
  boxQuantity: number;
  shippingAddress: RescueOrgShippingAddress;
  notes?: string | null;
}

export interface RescueCreateNanoOrderData {
  rescueCreateNanoOrder: {
    ok: boolean;
    orderId: string;
  };
}

/** Submit a nano-microchip-order request for the current rescue. */
export const rescueCreateNanoOrderMutation = gql`
  mutation rescueCreateNanoOrder($input: RescueCreateNanoOrderInput!) {
    rescueCreateNanoOrder(input: $input) {
      ok
      orderId
    }
  }
`;

// ── Chip condition mutation ─────────────────────

/**
 * Update a chip's condition (e.g. mark as DAMAGED, DECEASED_PET, MISSING).
 * Non-WORKING chips are filtered out of all reseller chip lists by the backend,
 * so this is effectively a one-way "remove from board" action.
 */
export const updateChipConditionMutation = gql`
  mutation rescueUpdateChipCondition($chipId: ID!, $chipCondition: ChipCondition!) {
    updateChipCondition(input: { chipId: $chipId, chipCondition: $chipCondition }) {
      id
      shortId
      serialNumber
      chipCondition
    }
  }
`;

// ── Rescue device mutations ─────────────────────

/** Create a foster transfer — moves pet to a foster caretaker. */
export const rescueCreatePetFosterTransferMutation = gql`
  mutation rescueCreatePetFosterTransfer($input: RescueCreatePetFosterTransferInput!) {
    rescueCreatePetFosterTransfer(input: $input) {
      __typename
    }
  }
`;

/** Recall a pet from a foster back to the rescue org. */
export const rescueRecallPetFosterTransferMutation = gql`
  mutation rescueRecallPetFosterTransfer($input: RescueRecallPetFosterTransferInput!) {
    rescueRecallPetFosterTransfer(input: $input) {
      __typename
    }
  }
`;

/** Unassign a device from a pet (Remove Pet). */
export const unassignDeviceMutation = gql`
  mutation unassignDevice($moduleId: String!, $petId: ID!) {
    unassignDevice(moduleId: $moduleId, petId: $petId) {
      id
    }
  }
`;

export const rescueCreatePetAdoptionTransferMutation = gql`
  mutation rescueCreatePetAdoptionTransfer($input: RescueCreatePetAdoptionTransferInput!) {
    rescueCreatePetAdoptionTransfer(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/** Cancel a pending adoption transfer — returns the device to the rescue. */
export const rescueCancelPetAdoptionTransferMutation = gql`
  mutation rescueCancelPetAdoptionTransfer($input: RescueCancelPetAdoptionTransferInput!) {
    rescueCancelPetAdoptionTransfer(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/**
 * Mark a stalled pending adoption as "not recovered" — the rescue steps out of
 * the adoption household, but the collar stays attached to its pet so a future
 * owner can still claim it and set up a subscription.
 */
export const rescueMarkAdoptionTransferNotRecoveredMutation = gql`
  mutation rescueMarkAdoptionTransferNotRecovered($input: RescueMarkAdoptionTransferNotRecoveredInput!) {
    rescueMarkAdoptionTransferNotRecovered(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/**
 * Mark a stalled pending adoption as "recovered" — the rescue got the collar
 * back. Closes the transfer and returns the device to unassigned stock.
 */
export const rescueMarkAdoptionTransferRecoveredMutation = gql`
  mutation rescueMarkAdoptionTransferRecovered($input: RescueMarkAdoptionTransferRecoveredInput!) {
    rescueMarkAdoptionTransferRecovered(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/** Send an SMS to a foster holder or adopter. */
export const rescueSendSmsMutation = gql`
  mutation rescueSendSms($input: RescueSendSmsInput!) {
    rescueSendSms(input: $input) {
      ok
    }
  }
`;

// ── Devices program enrollment ─────────────────

export interface RescueDevicesProgramData {
  rescueDevicesProgram: { enrolled: boolean };
}

export const rescueDevicesProgramQuery = gql`
  query rescueDevicesProgram {
    rescueDevicesProgram {
      enrolled
    }
  }
`;

export const rescueEnrollDevicesProgramMutation = gql`
  mutation rescueEnrollDevicesProgram {
    rescueEnrollDevicesProgram {
      enrolled
    }
  }
`;

// Self-serve rescue enrollment reuses the existing registerChipResellerV2 mutation: it creates a
// RESCUE-typed reseller (Accepted, unverified) for the signed-in user from the submitted form and
// auto-enrolls them in the rescue org group. We send `name` = the rescue household name.
export interface RescueResellerContactInput {
  firstName: string;
  lastName?: string | null;
  primaryPhone?: string | null;
  primaryEmail: string;
  website?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country: string;
}

export interface RegisterChipResellerV2Input {
  name: string;
  /** ResellerType values; rescue enrollment sends ['RESCUE']. */
  types: string[];
  /** ResellerPetInterest values; sent empty for rescue enrollment. */
  petInterestTypes: string[];
  contactInfo: RescueResellerContactInput;
  shippingAddress: RescueOrgShippingAddress;
}

export interface RegisterChipResellerV2Data {
  registerChipResellerV2: {
    id: string;
    name: string;
    verified: boolean;
  };
}

export const registerChipResellerV2Mutation = gql`
  mutation registerChipResellerV2($input: RegisterChipResellerInputV2!) {
    registerChipResellerV2(input: $input) {
      id
      name
      verified
    }
  }
`;

export const rescueUnenrollDevicesProgramMutation = gql`
  mutation rescueUnenrollDevicesProgram {
    rescueUnenrollDevicesProgram {
      enrolled
    }
  }
`;

// ── Assign device to pet ───────────────────────

/** Assign a GPS device to a pet by module ID. */
export const assignDeviceMutation = gql`
  mutation assignDevice($moduleId: String!, $petId: ID!) {
    assignDevice(moduleId: $moduleId, petId: $petId) {
      id
      name
    }
  }
`;

/**
 * Move a device already attached to one pet onto another, atomically. Unlike
 * `assignDevice`, the backend's `reassignDevice` calls `removeDeviceFromPet`
 * on the source pet before assigning to the target, so it works even when the
 * device has an active subscription that `isAssignable` would otherwise refuse.
 */
export const reassignDeviceMutation = gql`
  mutation reassignDevice($moduleId: String!, $toPetId: ID!) {
    reassignDevice(moduleId: $moduleId, toPetId: $toPetId) {
      id
      name
    }
  }
`;

export interface CheckDeviceAssignableData {
  checkDeviceAssignable: {
    assignable: boolean;
    reason?: string | null;
  };
}

/**
 * Reports whether the backend currently considers a device assignable. Used
 * client-side to gate the rescue's "assign device by serial" flow against
 * silently stealing a device off another household. Note: the backend's
 * `module:assignableWithoutSubscription` flag treats no-active-subscription
 * devices as assignable even when attached to another pet, so this gate
 * doesn't catch that edge case.
 */
export const checkDeviceAssignableQuery = gql`
  query checkDeviceAssignable($moduleId: String!) {
    checkDeviceAssignable(moduleId: $moduleId) {
      assignable
      reason
    }
  }
`;

/** Remove a pet from the household. */
export const removePetMutation = gql`
  mutation removePet($subjectId: ID!) {
    removePet(subjectId: $subjectId)
  }
`;

// ── Remove chip from pet ───────────────────────

/**
 * Detach a chip from its currently-attached pet and return it to the rescue's
 * stock (SHIPPED status). Clears both pet and owner/contact info.
 *
 * Used when a chip was incorrectly registered to a pet and needs to be made
 * available again. Distinct from `recallChip` (which clears only the owner
 * and preserves pet info) and `reassignChipToReseller` (which is for
 * claiming a chip from a different reseller).
 */
export const removeChipFromPetMutation = gql`
  mutation removeChipFromPet($input: RemoveChipFromPetInput!) {
    removeChipFromPet(input: $input) {
      id
      serialNumber
      shortId
    }
  }
`;

export interface RemoveChipFromPetInput {
  chipId: string;
}

export interface RemoveChipFromPetData {
  removeChipFromPet: {
    id: string;
    serialNumber: string;
    shortId: string;
  };
}

// ── Rescue pets (for pet selection) ────────────

export interface RescuePetOption {
  id: string;
  name: string;
  hasDevice: boolean;
}

export interface RescuePetGql {
  id: string;
  name: string;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  updatedAt?: string | null;
  additionalInfo?: string | null;
  device?: { id: string; moduleId: string; lastConnectionState?: { date: string } | null } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  breed?: { name: string } | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
}

export interface RescuePetsData {
  currentUser: {
    id: string;
    userHouseholds: {
      household: {
        id: string;
        type: string;
        pets: RescuePetGql[];
      };
    }[];
  };
}

/** Fetch all pets in the user's RESCUE household for selection dropdowns. */
export const rescuePetsQuery = gql`
  query rescuePets {
    currentUser {
      id
      userHouseholds {
        household {
          id
          type
          pets {
            id
            name
            gender
            species
            weight
            color
            additionalInfo
            updatedAt
            device {
              id
              moduleId
              lastConnectionState {
                date
              }
            }
            chip {
              id
              shortId
              serialNumber
              latestContactHistoryEntry {
                contactInfo {
                  primaryEmail
                }
              }
            }
            breed {
              name
            }
            yearOfBirth
            monthOfBirth
            dayOfBirth
          }
        }
      }
    }
  }
`;

// ── Breeds (for pet creation) ──────────────────

export interface BreedOption {
  id: string;
  name: string;
  species: string;
}

export interface AllBreedsData {
  allBreeds: BreedOption[];
}

/** Fetch all breeds, optionally filtered by species. */
export const allBreedsQuery = gql`
  query rescueAllBreeds($species: Species) {
    allBreeds(species: $species) {
      id
      name
      species
    }
  }
`;

// ── Create pet (no device) ─────────────────────

/** Create a new pet in the rescue household without assigning a device. */
export const createPetMutation = gql`
  mutation createPet($input: CreatePetInput!) {
    createPet(input: $input) {
      id
      name
    }
  }
`;

/** Update an existing pet's profile fields. */
export const updatePetMutation = gql`
  mutation rescueUpdatePet($input: UpdatePetInput!) {
    updatePet(input: $input) {
      id
      name
    }
  }
`;

// ── Org info (lightweight) ──────────────────────

/** Lightweight query for the rescue org name. Fetches chipReseller name
 *  and falls back to the user's RESCUE-type household name. */
export const rescueOrgInfoQuery = gql`
  query rescueOrgInfo {
    currentUser {
      id
      isAdmin
      firstName
      lastName
      email
      phoneNumber
      chipReseller {
        id
        name
        taxExemptType
        contactInfo {
          firstName
          lastName
        }
        shippingAddress {
          line1
          line2
          city
          state
          country
          zip
        }
        verified
      }
      userHouseholds {
        household {
          id
          name
          type
        }
      }
    }
  }
`;

export interface RescueUnassignedInventoryDeviceItemGql {
  moduleId: string;
  type?: string | null;
  batteryPercent: number | null;
  isOnline: boolean;
  lastConnectionDate: string;
}

export interface RescueUnassignedDeviceInventoryData {
  rescueUnassignedDeviceInventory: {
    items: RescueUnassignedInventoryDeviceItemGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

export const rescueUnassignedDeviceInventoryQuery = gql`
  query rescueUnassignedDeviceInventory($input: RescueUnassignedInventoryDevicesInput!) {
    rescueUnassignedDeviceInventory(input: $input) {
      items {
        moduleId
        type
        batteryPercent
        isOnline
        lastConnectionDate
      }
      totalCount
      hasMore
    }
  }
`;

export interface RescueOrgInfoData {
  currentUser: {
    id: string;
    isAdmin: boolean;
    firstName: string;
    lastName: string | null;
    email: string;
    phoneNumber: string | null;
    chipReseller?: {
      id: string;
      name: string;
      taxExemptType?: ResellerTaxExemptType | null;
      contactInfo?: { firstName: string; lastName: string | null } | null;
      shippingAddress?: RescueOrgShippingAddress | null;
      verified: boolean;
    } | null;
    userHouseholds: {
      household: {
        id: string;
        name: string | null;
        type: string;
      };
    }[];
  };
}

export interface ExternalChipLookupData {
  resellerChip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    status: string;
    updatedAt: string;
    pet?: {
      id: string;
      name: string;
      gender: string;
      species?: string | null;
      isPurebred: boolean;
      color?: string | null;
      yearOfBirth: number;
      monthOfBirth?: number | null;
      dayOfBirth?: number | null;
      weight: number;
      additionalInfo?: string | null;
      updatedAt: string;
      breed?: { id: string; name: string } | null;
    } | null;
    latestContactHistoryEntry?: {
      source: string;
      contactInfo: {
        firstName: string;
        lastName?: string | null;
        primaryPhone?: string | null;
        primaryEmail: string;
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        zipcode?: string | null;
      };
    } | null;
    reseller?: {
      id: string;
      name: string;
    } | null;
  } | null;
}

export interface ExternalStrangerChipLookupData {
  chip?: {
    id: string;
    serialNumber: string;
    shortId: string;
    hasOwner: boolean;
    isFiChip: boolean;
  } | null;
}
